// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { UserRole } from "graphql/api";
import { CloudSettings, DeletionStrategy, ReportsSettings } from "dbtypes";
import { DataState } from "types";
import { IAction, ActionType, raiseAction } from "actions";
import { IState } from "states/states";
import { isStateDirty, toDropdownItems } from "guiutils";
import Dropdown from "./Dropdown";

interface ISiteSettingsExtProps {
  cloudSettings: CloudSettings | undefined;
  onSetCloudSettings: (cloudSettings: CloudSettings) => void;
}

interface ISiteSettingsProps {}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const isAdminUser = [UserRole.admin, UserRole.root].includes(curUser?.role ?? UserRole.viewer);

  const props: ISiteSettingsProps = {
    isAdminUser,
  };
  return props;
};

interface ISiteSettingsDispatch {
  onChanges: (dataState: DataState) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISiteSettingsDispatch = {
    onChanges: (dataState: DataState) => {
      if (!isStateDirty(dataState)) {
        dispatch(raiseAction(ActionType.ACT_SET_PAGE_DIRTY, true));
      }
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectDeletionCaption: {
    id: "Select deletion strategy",
  },
});

class SiteSettingsComponent extends React.Component<
  ISiteSettingsProps &
    ISiteSettingsExtProps &
    ISiteSettingsDispatch &
    WrappedComponentProps &
    RoutedProps
> {
  public render() {
    // render drop-down for DeletionStrategy
    const deletionItem = [
      DeletionStrategy.SMALL,
      DeletionStrategy.LARGE,
      DeletionStrategy.UNLIMITED,
    ];
    const dropdownItems = toDropdownItems(deletionItem, "DeletionStrategy", (name, value) =>
      this.props.onSetCloudSettings({
        ...this.props.cloudSettings,
        deletionStrategy: value,
      }),
    );
    return (
      <div className="fullwidthwhite">
        <table>
          <tbody>
            <tr className="mt-2">
              <td>
                <FormattedMessage id="Database Policy" />
              </td>
              <td>
                <Dropdown
                  items={dropdownItems}
                  caption={this.props.intl.formatMessage(messages.selectDeletionCaption)}
                  curItem={
                    this.props.cloudSettings?.deletionStrategy ? (
                      <FormattedMessage
                        id={`DeletionStrategy_${this.props.cloudSettings.deletionStrategy}`}
                      />
                    ) : (
                      this.props.intl.formatMessage(messages.selectDeletionCaption)
                    )
                  }
                />
              </td>
            </tr>
            <tr className="mt-1">
              <td>
                <FormattedMessage id="End Customer" />
              </td>
              <td>
                <input
                  className="fullwidth"
                  type="text"
                  value={this.props.cloudSettings?.reportsSettings?.endCustomer ?? ""}
                  onChange={(event) => this.setField("endCustomer", event.currentTarget.value)}
                />
              </td>
            </tr>
            <tr className="mt-1">
              <td>
                <FormattedMessage id="Order Number" />
              </td>
              <td>
                <input
                  className="fullwidth"
                  type="text"
                  value={this.props.cloudSettings?.reportsSettings?.orderNumber ?? ""}
                  onChange={(event) => this.setField("orderNumber", event.currentTarget.value)}
                />
              </td>
            </tr>
            <tr className="mt-1">
              <td>
                <FormattedMessage id="Service Type" />
              </td>
              <td>
                <input
                  className="fullwidth"
                  type="text"
                  value={this.props.cloudSettings?.reportsSettings?.serviceType ?? ""}
                  onChange={(event) => this.setField("serviceType", event.currentTarget.value)}
                />
              </td>
            </tr>
            <tr className="mt-1">
              <td style={{ verticalAlign: "top" }}>
                <FormattedMessage id="Comments" />
              </td>
              <td>
                <textarea
                  className="fullwidth"
                  style={{ resize: "both" }}
                  value={this.props.cloudSettings?.reportsSettings?.annotation ?? ""}
                  onChange={(event) => this.setField("annotation", event.currentTarget.value)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  private setField(fieldName: keyof ReportsSettings, value: string) {
    this.props.onSetCloudSettings({
      ...this.props.cloudSettings,
      reportsSettings: {
        ...this.props.cloudSettings?.reportsSettings,
        [fieldName]: value,
      },
    });
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(SiteSettingsComponent)),
);
